import { useState, useEffect, useRef } from "react";

/**
 * Custom hook for detecting scroll direction and hiding UI elements
 * when scrolling down, showing them when scrolling up.
 *
 * @param {Object} options - Configuration options
 * @param {number} options.threshold - Minimum scroll delta to trigger state change (default: 10)
 * @param {number} options.scrollThreshold - Minimum scroll position before hiding starts (default: 100)
 * @returns {boolean} - Whether the element should be hidden
 */
export function useHideOnScroll(options = {}) {
  const { threshold = 10, scrollThreshold = 100 } = options;
  const [hidden, setHidden] = useState(false);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    const updateScrollState = () => {
      const currentScrollY = window.scrollY;

      // Don't hide if we're near the top (handles iOS bounce scroll)
      if (currentScrollY <= 0) {
        setHidden(false);
        lastScrollY.current = currentScrollY;
        ticking.current = false;
        return;
      }

      const scrollDelta = currentScrollY - lastScrollY.current;

      // Only trigger after passing threshold to avoid jitter
      if (Math.abs(scrollDelta) < threshold) {
        ticking.current = false;
        return;
      }

      // Hide on scroll down (only after passing scrollThreshold)
      if (scrollDelta > 0 && currentScrollY > scrollThreshold) {
        setHidden(true);
      }
      // Show on scroll up
      else if (scrollDelta < 0) {
        setHidden(false);
      }

      lastScrollY.current = currentScrollY;
      ticking.current = false;
    };

    const handleScroll = () => {
      // Use requestAnimationFrame for smooth 60fps performance
      if (!ticking.current) {
        window.requestAnimationFrame(updateScrollState);
        ticking.current = true;
      }
    };

    // Use passive listener for better scroll performance
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [threshold, scrollThreshold]);

  // Reset hidden state when route changes (scroll position resets)
  useEffect(() => {
    const handleRouteChange = () => {
      setHidden(false);
      lastScrollY.current = 0;
    };

    // Listen for popstate (back/forward navigation)
    window.addEventListener("popstate", handleRouteChange);

    return () => {
      window.removeEventListener("popstate", handleRouteChange);
    };
  }, []);

  return hidden;
}

export default useHideOnScroll;
